// src/pages/CheckCard.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingScreen from '../components/LoadingScreen';
import CardNotExist from '../components/CardNotExist';
import EvoWishesLogo from '../assets/evo_wishes.png';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.146:8000/api';

const MAX_ATTEMPTS = 3;
const LOCKOUT_DURATION = 10 * 60 * 1000; // 10 minutes
const STORAGE_KEY = 'auth_attempts';
const AUTH_KEY = 'authenticated_card'; // New key for successful auth

const CheckCard = () => {
  const { card_uid } = useParams();
  const navigate = useNavigate();

  const [cardExists, setCardExists] = useState(null);
  const [cardData, setCardData] = useState(null); // New state for card data, including front_picture
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [agree, setAgree] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockedOutUntil, setLockedOutUntil] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0); // Countdown state
  const [loading, setLoading] = useState(true);
  const [frontPicture, setFrontPicture] = useState(null); // For LoadingScreen background

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000); // 3-second loading delay

    // Load attempts from localStorage
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    if (stored.card_uid === card_uid) {
      setAttempts(stored.attempts || 0);
      setLockedOutUntil(stored.lockedOutUntil || null);
    }

    // Fetch card existence
    axios
      .get(`${API_BASE_URL}/card_uid/${card_uid}/`)
      .then((response) => {
        const data = response.data;
       if (response.data.front_picture) {
          setFrontPicture(`${API_BASE_URL.replace('/api', '')}${response.data.front_picture}`);
        }       
        setCardData(data); // Save full card data
        setCardExists(data.exists !== false); // Updated check for consistency
      })
      .catch((err) => {
        setCardExists(false);
      });

    return () => clearTimeout(timer); // Cleanup timer
  }, [card_uid]);

  useEffect(() => {
    // Persist attempts/lockout
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ card_uid, attempts, lockedOutUntil }));
  }, [attempts, lockedOutUntil, card_uid]);

  // Countdown timer effect
  useEffect(() => {
    let interval;
    if (lockedOutUntil && lockedOutUntil > Date.now()) {
      interval = setInterval(() => {
        const remaining = Math.ceil((lockedOutUntil - Date.now()) / 1000);
        setRemainingTime(remaining);
        if (remaining <= 0) {
          clearInterval(interval);
          resetAttempts();
        } else {
          setError(`Твърде много опити. Опитайте след ${formatRemainingTime(remaining)}.`);
        }
      }, 1000);
      // Initial error set
      const initialRemaining = Math.ceil((lockedOutUntil - Date.now()) / 1000);
      setRemainingTime(initialRemaining);
      setError(`Твърде много опити. Опитайте след ${formatRemainingTime(initialRemaining)}.`);
    }
    return () => clearInterval(interval);
  }, [lockedOutUntil]);

  const formatRemainingTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const resetAttempts = () => {
    setAttempts(0);
    setLockedOutUntil(null);
    setRemainingTime(0);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const handleFailedAttempt = () => {
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    if (newAttempts >= MAX_ATTEMPTS) {
      const lockoutTime = Date.now() + LOCKOUT_DURATION;
      setLockedOutUntil(lockoutTime);
      const initialRemaining = Math.ceil(LOCKOUT_DURATION / 1000);
      setRemainingTime(initialRemaining);
      setError(`Твърде много опити. Опитайте след ${formatRemainingTime(initialRemaining)}.`);
    } else {
      setError('Грешна парола');
    }
  };

  const checkVideoAvailability = () => {
    axios
      .get(`${API_BASE_URL}/card_uid/${card_uid}/`)
      .then((response) => {
        if (response.data.video) {
          navigate(`/video/${card_uid}`);
        } else {
          navigate(`/upload/${card_uid}`);
        }
      })
      .catch((error) => {
        setError('Грешка при проверка на видео');
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError("Моля, въведете парола.");
      return;
    }

    if (!agree) {
      setError("Трябва да се съгласите с общите условия.");
      return;
    }

    if (lockedOutUntil && lockedOutUntil > Date.now()) {
      return;
    }

    axios
      .post(`${API_BASE_URL}/auth_card/`, { card_uid, password })
      .then((response) => {
        if (response.data.isAuthenticated) {
          resetAttempts();
          localStorage.setItem(`${AUTH_KEY}_${card_uid}`, 'true'); // Set auth key on success
          checkVideoAvailability();
        } else {
          handleFailedAttempt();
        }
      })
      .catch((error) => {
        setError('Грешка при аутентикация');
      });
  };

  if (loading) return <LoadingScreen backgroundImage={frontPicture} />;
  if (cardExists === false) return <CardNotExist />;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4" 
      style={{ 
        backgroundImage: cardData && cardData.front_picture ? `url(${API_BASE_URL.replace('/api', '')}${cardData.front_picture})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="w-full max-w-md bg-white bg-opacity-50 backdrop-filter backdrop-blur-md rounded-lg shadow-lg p-6 border border-white/30"> {/* Glassy look: transparent, blur, subtle border */}
        <div className="flex justify-center mb-4">
          <img src={EvoWishesLogo} alt="EvoWishes Logo" className="h-16 w-auto" />
        </div>
        <p className="text-center text-gray-600 mb-6">Моля, въведете паролата намираща се под баркода.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
        <input
            type="number"
            className="w-full px-4 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500 bg-white/80" // Slight transparency for input
            placeholder="Въведете парола"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <div className="flex items-center">
            <input id="agreeToTerms" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mr-2" />
            <label htmlFor="agreeToTerms" className="text-gray-600">
              Съгласен съм с&nbsp;<a href="/terms" className="text-red-500 underline">общите условия</a>
            </label>
          </div>
          <button
            type="submit"
            className="w-full py-2 bg-red-500 text-white font-semibold rounded hover:bg-red-600 disabled:opacity-50"
            disabled={lockedOutUntil && lockedOutUntil > Date.now()}
          >
            Зареди
          </button>
          {error && <div className="mt-2 text-red-600 font-medium">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default CheckCard;

